import { useCallback, useEffect, useMemo, useState } from 'react';
import { useToast } from '@practium/ui';
import { getMockFoliarAnalysis, mockAdjustDemand } from '../mocks/foliarAdjustment.mock';
import type { FertilizationPlanAdvice } from '../types';
import { mapPlanningAdjustedDemand, type PlanningSectorResult } from '../utils/planningTableMapper';
import type { PlanningDemandView } from './usePlanningForm';

interface UseDemandAdjustmentOptions {
	advice: FertilizationPlanAdvice;
	sectors: PlanningSectorResult[];
	demandPeriods: string[];
	products: Array<{ _id: string; name?: string; commercialName?: string }>;
}

export const useDemandAdjustment = ({
	advice,
	sectors,
	demandPeriods,
	products,
}: UseDemandAdjustmentOptions) => {
	const toast = useToast();
	const [isAdjusting, setIsAdjusting] = useState(false);
	const [selectedPeriods, setSelectedPeriods] = useState<string[]>([]);
	const [adjustedSectors, setAdjustedSectors] = useState<PlanningSectorResult[]>([]);
	const [adjustedView, setAdjustedView] = useState<PlanningDemandView>('hectare');
	const [isApplying, setIsApplying] = useState(false);

	const foliarAnalysis = useMemo(() => getMockFoliarAnalysis(advice), [advice]);

	useEffect(() => {
		setIsAdjusting(false);
		setSelectedPeriods([]);
		setAdjustedSectors([]);
	}, [sectors]);

	const toggleAdjusting = () => {
		setIsAdjusting((current) => !current);
		setSelectedPeriods([]);
	};

	const togglePeriod = useCallback((period: string) => {
		setSelectedPeriods((current) =>
			current.includes(period) ? current.filter((item) => item !== period) : [...current, period]
		);
	}, []);

	const toggleAllPeriods = useCallback(() => {
		setSelectedPeriods((current) => (current.length === demandPeriods.length ? [] : demandPeriods));
	}, [demandPeriods]);

	const applyAdjustment = async () => {
		if (!selectedPeriods.length) return;
		setIsApplying(true);
		try {
			setAdjustedSectors(await mockAdjustDemand(sectors, selectedPeriods));
			setIsAdjusting(false);
			setSelectedPeriods([]);
		} catch (error) {
			toast.error(error instanceof Error ? error.message : 'Erro ao ajustar a demanda.');
		} finally {
			setIsApplying(false);
		}
	};

	const adjustedDemand = useMemo(
		() =>
			adjustedSectors.length
				? mapPlanningAdjustedDemand(sectors, adjustedSectors, adjustedView === 'hectare', products)
				: null,
		[adjustedSectors, adjustedView, products, sectors]
	);

	return {
		isAdjusting,
		toggleAdjusting,
		selectedPeriods,
		togglePeriod,
		toggleAllPeriods,
		foliarAnalysis,
		applyAdjustment,
		isApplying,
		adjustedSectors,
		adjustedDemand,
		adjustedView,
		setAdjustedView,
	};
};
